import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";

const BASE_URL =
  process.env.REACT_APP_API_URL || "https://stage.getrevibe.app/api/";

const getBanners = async ({ page = 1, limit = 10 } = {}) => {
  const url = new URL("banners", BASE_URL);
  url.searchParams.set("page", page);
  url.searchParams.set("limit", limit);

  const response = await fetch(url.toString(), {
    method: "GET",
    headers: { "Content-Type": "application/json" },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.message || `Failed to fetch banners (${response.status})`);
  }
  return response.json();
};

const getBannerImageUrl = (filename) => {
  if (!filename) return "";
  const baseUrl = BASE_URL.replace(/\/$/, "");
  return `${baseUrl}/uploads/image/${filename}`;
};

const getMobileImageUrl  = (banner) => getBannerImageUrl(banner.img);
const getDesktopImageUrl = (banner) => getBannerImageUrl(banner.imgDesktop || banner.img);

const BannerCarousel = () => {
  const [banners, setBanners] = useState([]);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);

  const hasActiveSubscription = (() => {
    try {
      const sub = JSON.parse(localStorage.getItem("userSubscription") || "null");
      return sub?.status === "Active";
    } catch {
      return false;
    }
  })();

  /**
   * Cloned slides layout:
   *   [lastClone, banner1, banner2, ..., bannerN, firstClone]
   *   index:  0      1       2            N        N+1
   *
   * trackIndex starts at 1 (first real banner).
   * When trackIndex reaches N+1 (firstClone) → silent jump to 1.
   * When trackIndex reaches 0  (lastClone)   → silent jump to N.
   */
  const [trackIndex, setTrackIndex] = useState(1);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const autoPlayRef = useRef(null);
  const isJumping = useRef(false);

  useEffect(() => {
    const fetchBanners = async () => {
      try {
        setLoading(true);
        const data = await getBanners({ page: 1, limit: 10 });
        const activeBanners = (data.banners || []).filter((b) => b.status === "Active");
        setBanners(activeBanners);
        setTrackIndex(1);
        setError(null);
      } catch (err) {
        setError(err.message);
        setBanners([]);
      } finally {
        setLoading(false);
      }
    };
    fetchBanners();
  }, []);

  const clonedSlides = banners.length > 0
    ? [banners[banners.length - 1], ...banners, banners[0]]
    : [];

  const goNext = useCallback(() => {
    if (isJumping.current || banners.length === 0) return;
    setIsTransitioning(true);
    setTrackIndex((prev) => prev + 1);
  }, [banners.length]);

  const goPrev = useCallback(() => {
    if (isJumping.current || banners.length === 0) return;
    setIsTransitioning(true);
    setTrackIndex((prev) => prev - 1);
  }, [banners.length]);

  // After CSS transition ends, silently snap back if on a clone
  const handleTransitionEnd = useCallback(() => {
    const n = banners.length;
    if (trackIndex === n + 1) {
      // first-clone → jump to real first
      isJumping.current = true;
      setIsTransitioning(false);
      setTrackIndex(1);
      setTimeout(() => { isJumping.current = false; }, 50);
    } else if (trackIndex === 0) {
      // last-clone → jump to real last
      isJumping.current = true;
      setIsTransitioning(false);
      setTrackIndex(n);
      setTimeout(() => { isJumping.current = false; }, 50);
    }
  }, [trackIndex, banners.length]);

  // Autoplay — 3000ms
  useEffect(() => {
    if (isAutoPlaying && banners.length > 1) {
      autoPlayRef.current = setInterval(goNext, 3000);
    }
    return () => { if (autoPlayRef.current) clearInterval(autoPlayRef.current); };
  }, [isAutoPlaying, banners.length, goNext]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "ArrowLeft") goPrev();
      else if (e.key === "ArrowRight") goNext();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [goPrev, goNext]);

  // Touch swipe
  const handleTouchStart = (e) => setTouchStart(e.targetTouches[0].clientX);
  const handleTouchMove  = (e) => setTouchEnd(e.targetTouches[0].clientX);
  const handleTouchEnd   = () => {
    if (touchStart - touchEnd > 75) goNext();
    if (touchStart - touchEnd < -75) goPrev();
    setTouchStart(0);
    setTouchEnd(0);
  };

  // ─── Loading ───────────────────────────────────────────
  if (loading) {
    return (
      <>
        <div className="md:hidden w-full bg-gray-900 flex items-center justify-center" style={{ height: "calc(100vh - 180px)" }}>
          <div className="text-center">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#c8f000] mx-auto mb-3" />
            <p className="text-gray-400 text-sm">Loading...</p>
          </div>
        </div>
        <div className="hidden md:flex relative w-full h-64 md:h-96 lg:h-[500px] bg-gray-100 items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4" />
            <p className="text-gray-600">Loading banners...</p>
          </div>
        </div>
      </>
    );
  }

  // ─── Error ─────────────────────────────────────────────
  if (error) {
    return (
      <>
        <div className="md:hidden w-full bg-gray-900 flex items-center justify-center" style={{ height: "calc(100vh - 180px)" }}>
          <div className="text-center text-red-400 px-6">
            <p className="text-base font-semibold mb-1">Error loading banners</p>
            <p className="text-sm opacity-70">{error}</p>
          </div>
        </div>
        <div className="hidden md:flex relative w-full h-64 md:h-96 lg:h-[500px] bg-gray-100 items-center justify-center">
          <div className="text-center text-red-600">
            <p className="text-lg font-semibold mb-2">Error loading banners</p>
            <p className="text-sm">{error}</p>
          </div>
        </div>
      </>
    );
  }

  // ─── Empty ─────────────────────────────────────────────
  if (banners.length === 0) {
    return (
      <>
        <div className="md:hidden w-full bg-gray-900 flex items-center justify-center" style={{ height: "calc(100vh - 180px)" }}>
          <p className="text-gray-400 text-base">No active banners available</p>
        </div>
        <div className="hidden md:flex relative w-full h-64 md:h-96 lg:h-[500px] bg-gray-100 items-center justify-center">
          <p className="text-gray-500 text-lg">No active banners available</p>
        </div>
      </>
    );
  }

  const slideStyle = {
    transform: `translateX(-${trackIndex * 100}%)`,
    transition: isTransitioning ? "transform 500ms ease-out" : "none",
  };

  const renderSlide = (banner, key, isMobile) => (
    <div key={key} className="relative flex-shrink-0 w-full h-full bg-gray-900">
      <img
        src={isMobile ? getMobileImageUrl(banner) : getDesktopImageUrl(banner)}
        alt={banner.title || "Banner"}
        className={`w-full h-full object-cover${isMobile ? "" : " opacity-90"}`}
        draggable={false}
        onError={(e) => {
          if (!isMobile) {
            const mobileUrl = getMobileImageUrl(banner);
            if (mobileUrl && e.target.src !== mobileUrl) {
              e.target.src = mobileUrl;
              return;
            }
          }
          e.target.src = isMobile
            ? "https://via.placeholder.com/800x1200?text=Image+Not+Found"
            : "https://via.placeholder.com/1920x600?text=Image+Not+Found";
          e.target.onerror = null;
        }}
      />
      {isMobile
        ? <div className="absolute inset-x-0 top-0 h-40 bg-gradient-to-b from-black/40 to-transparent pointer-events-none" />
        : <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/30 to-transparent" />
      }
    </div>
  );

  return (
    <>
      {/* ══════════════════════════════════════════════════
          MOBILE CAROUSEL — banner.img
      ══════════════════════════════════════════════════ */}
      <div
        className="md:hidden relative w-full overflow-hidden bg-black -mt-16"
        style={{ height: "calc(100vh - 80px)" }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <div
          className="flex h-full"
          style={slideStyle}
          onTransitionEnd={handleTransitionEnd}
        >
          {clonedSlides.map((banner, i) =>
            renderSlide(banner, `mobile-${i}`, true)
          )}
        </div>
      </div>

      {/* ══════════════════════════════════════════════════
          DESKTOP CAROUSEL — banner.imgDesktop
      ══════════════════════════════════════════════════ */}
      <div
        className="hidden md:block relative w-full overflow-hidden bg-black"
        onMouseEnter={() => setIsAutoPlaying(false)}
        onMouseLeave={() => setIsAutoPlaying(true)}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <div className="relative w-full h-64 md:h-96 lg:h-[500px]">
          <div
            className="flex h-full"
            style={slideStyle}
            onTransitionEnd={handleTransitionEnd}
          >
            {clonedSlides.map((banner, i) =>
              renderSlide(banner, `desktop-${i}`, false)
            )}
          </div>
        </div>
      </div>

      {!hasActiveSubscription && (
        <section className="w-full py-6 bg-gray-50 flex flex-col items-center justify-center text-center px-6">
          <button
            onClick={() => navigate("/plan")}
            className="bg-[#c8f000] text-black font-semibold px-10 py-4 rounded-full shadow-md hover:scale-105 transition duration-300"
          >
            Subscribe Now
          </button>
        </section>
      )}
    </>
  );
};

export default BannerCarousel;